import { normalizeFieldName } from "./cpFieldRules";

export const CP_ROLES = { physician: "Physician", operator: "Clinician" };

export function newDraftKey() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function emptyCpDraft(id = newDraftKey()) {
  return { id, revision: 0, name: "", document_kind: "cp_agreement", signing_scope: "per_state", state_ids: [], license_classes: [], signer_roles: ["physician", "operator"], pdf: null, fields: [] };
}

export function unsupportedFieldLabel(label) {
  return /\b(ssn|social security|dob|date of birth|birth\s*date|payment|credit card|card\s*(number|holder)|debit card|cvv|cvc|bank account|routing number)\b/i.test(normalizeFieldName(label));
}

export function missingPolicy(field, sources) {
  if (field.value_source === "record" && field.source_key === "license_number") return "block_document";
  const source = sources.find((entry) => entry.key === field.source_key);
  // Sign marks the sources that, as on the CP agreement, print blank when missing even if required.
  if (!field.required || (field.value_source === "record" && source?.blank_when_missing === true)) return "leave_blank";
  return field.value_source === "signer" || source?.editable ? "ask_signer" : "hold_required";
}

// Normalize known policy changes in memory; opening a draft never saves it:
// - old drafts held required name/email values before those sources became editable;
// - Sign stores hold_required (or Sign #93's ask_signer for name/email) for a required source
//   that now prints blank when missing.
export function normalizeCpDraftPolicies(draft, sources) {
  if (!draft) return draft;
  return { ...draft, fields: draft.fields.map((field) => {
    const source = sources.find((entry) => entry.key === field.source_key);
    const known = field.value_source === "record" && field.required === true && ["physician", "operator"].includes(field.signer_role)
      && Boolean(source) && source.field_type === field.field_type && source.roles.includes(field.signer_role);
    const identity = ["name", "email"].includes(field.source_key) && field.field_type === "text";
    if (known && source.blank_when_missing === true
      && (field.missing_policy === "hold_required" || (identity && field.missing_policy === "ask_signer"))) {
      return { ...field, missing_policy: "leave_blank" };
    }
    const editableIdentity = known && identity && field.missing_policy === "hold_required" && source.editable === true;
    return editableIdentity ? { ...field, missing_policy: "ask_signer" } : field;
  }) };
}

export function applyFieldSource(field, source, sources) {
  const next = source
    ? { ...field, value_source: "record", source_key: source.key, field_type: source.field_type }
    : { ...field, value_source: "signer", source_key: null };
  return { ...next, missing_policy: missingPolicy(next, sources) };
}

export function createDraftField(role, type, pageNumber, sources) {
  const field = { field_key: newDraftKey(), label: type === "signature" ? "Signature" : "New field", signer_role: role, field_type: type, value_source: "signer", source_key: null, required: true, missing_policy: "ask_signer", page_number: pageNumber, position_x: 0.1, position_y: 0.1, width: type === "checkbox" ? 0.04 : 0.28, height: type === "signature" ? 0.07 : 0.04 };
  return { ...field, missing_policy: missingPolicy(field, sources) };
}

// ENG-2114: Sign's fixed field list. A placed field is one of its items, once per signer.
const ITEM_KEYS = ["label", "field_type", "value_source", "source_key", "required"];
const matching = (field, items, keys) => items.find((item) => item.roles.includes(field.signer_role) && keys.every((key) => field[key] === item[key])) || null;
export function itemFor(field, items = []) {
  return matching(field, items, ITEM_KEYS);
}

export function createItemField(item, role, pageNumber, sources) {
  const base = createDraftField(role, item.field_type, pageNumber, sources);
  const field = { ...base, label: item.label, value_source: item.value_source, source_key: item.source_key, required: item.required,
    height: item.source_key === "address" ? 0.08 : base.height };
  return { ...field, missing_policy: missingPolicy(field, sources) };
}

export function placedItemKeys(fields, role, items = []) {
  return new Set(fields.filter((field) => field.signer_role === role).map((field) => itemFor(field, items)?.key).filter(Boolean));
}

const clamp = (value, low, high) => Math.min(Math.max(value, low), high);

// New CP geometry is a fraction of the displayed page, independent of zoom and rotation.
export function boundedGeometry(field, updates = {}) {
  const next = { ...field, ...updates };
  const width = clamp(Number(next.width) || 0.01, 0.01, 1);
  const height = clamp(Number(next.height) || 0.01, 0.01, 1);
  return { ...next, width, height, position_x: clamp(Number(next.position_x) || 0, 0, 1 - width), position_y: clamp(Number(next.position_y) || 0, 0, 1 - height) };
}

export function moveFieldByKey(field, key, renderedWidth, renderedHeight, shift = false) {
  const distance = shift ? 10 : 1;
  const moves = { ArrowLeft: [-distance / renderedWidth, 0], ArrowRight: [distance / renderedWidth, 0], ArrowUp: [0, -distance / renderedHeight], ArrowDown: [0, distance / renderedHeight] };
  const move = moves[key];
  return move ? boundedGeometry(field, { position_x: field.position_x + move[0], position_y: field.position_y + move[1] }) : field;
}

export async function measureCpPdfPages(pdf) {
  return Promise.all(Array.from({ length: pdf.numPages }, async (_, index) => {
    const page = await pdf.getPage(index + 1);
    const viewport = page.getViewport({ scale: 1 });
    return { page_number: index + 1, width: viewport.width, height: viewport.height, rotation: ((viewport.rotation % 360) + 360) % 360 };
  }));
}

export function samePdfPages(left, right) {
  return left.length === right.length && left.every((page, index) =>
    ["page_number", "width", "height", "rotation"].every((key) => page[key] === right[index][key]));
}

export function draftReviewGaps(draft, sources, items = []) {
  const gaps = [];
  if (!draft.name.trim()) gaps.push("Add a template name.");
  if (!draft.document_kind) gaps.push("Choose a document kind.");
  if (!draft.state_ids.length || !draft.license_classes.length) gaps.push("Choose a state and a clinician type.");
  if (!draft.pdf) gaps.push("Upload a PDF.");
  if (draft.pdf && !draft.pdf.pages.length) gaps.push("Wait for the PDF pages to load.");
  if (!draft.signer_roles.length) gaps.push("Choose who signs.");
  draft.signer_roles.forEach((role) => {
    const signatures = draft.fields.filter((field) => field.signer_role === role && field.field_type === "signature");
    if (!signatures.length) gaps.push(`Add a signature field for the ${CP_ROLES[role].toLowerCase()}.`);
    else if (!signatures.some((field) => field.required)) gaps.push(`Make a signature field required for the ${CP_ROLES[role].toLowerCase()}.`);
  });
  draft.fields.forEach((field) => {
    if (!field.label.trim()) gaps.push("Give every field a label.");
    if (unsupportedFieldLabel(field.label)) gaps.push(`Remove unsupported sensitive field: ${field.label}.`);
    if (!draft.signer_roles.includes(field.signer_role)) gaps.push(`${field.label}: restore its signer role or explicitly change the field's role.`);
    if (field.value_source === "record" && !sources.some((source) => source.key === field.source_key && source.roles.includes(field.signer_role))) gaps.push(`${field.label}: choose a supported record source for its signer.`);
  });
  // Same rule Sign applies when publishing; skipped when the list itself did not load.
  const placed = new Set();
  if (items.length) draft.fields.forEach((field) => {
    const item = itemFor(field, items);
    // An older field of a listed kind (its own label, or another required flag) names the item to use.
    const kin = !item && matching(field, items, ["field_type", "value_source", "source_key"]);
    const label = field.label.trim() || "A field";
    const gap = kin ? `${label} ${label === kin.label ? "has older settings" : `does not match the list's ${kin.label}`}. Remove it and add ${kin.label} from the list.`
      : !item ? `${label} is not on the field list. Remove it and add an item from the list.`
      : placed.has(`${field.signer_role}:${item.key}`) ? `${item.label} is placed more than once for the ${(CP_ROLES[field.signer_role] || field.signer_role).toLowerCase()}.` : null;
    placed.add(`${field.signer_role}:${item?.key}`);
    if (gap && !gaps.includes(gap)) gaps.push(gap);
  });
  return gaps;
}

export const MISSING_POLICY_TEXT = {
  block_document: "Hold this document until the license record is available.",
  hold_required: "Hold this document for record correction.",
  ask_signer: "Ask the signer to fill this in.",
  leave_blank: "Leave blank when missing.",
};
